import { Component } from '@theme/component';
import { ThemeEvents, CompareUpdateEvent } from '@theme/events';
import { ProductCompare } from '@theme/product-compare';

/**
 * A custom element that displays the compare icon and its item count badge.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} compareBubble - The compare bubble element.
 * @property {HTMLElement} compareBubbleCount - The compare bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CompareIcon extends Component {
  requiredRefs = ['compareBubble', 'compareBubbleCount'];

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.compareUpdate, this.onCompareUpdate);
    this.#render(ProductCompare.getProducts().length);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.compareUpdate, this.onCompareUpdate);
  }

  /**
   * Re-syncs with the compare list when re-rendered by the Section Rendering API
   * (e.g. the header's post-load hydration fetch, see sections/header.liquid).
   * The freshly-fetched markup has no knowledge of the localStorage-backed compare
   * list, so it always morphs in with an empty/hidden bubble unless corrected here.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#render(ProductCompare.getProducts().length);
  }

  /**
   * Handles the compare update event.
   * @param {CompareUpdateEvent} event - The compare update event.
   */
  onCompareUpdate = (event) => {
    this.#render(event.detail.products.length);
  };

  /**
   * @param {number} count
   */
  #render(count) {
    const { compareBubble, compareBubbleCount } = this.refs;

    compareBubbleCount.textContent = String(count);
    compareBubble.classList.toggle('hidden', count === 0);
    this.classList.toggle('header-actions__compare-icon--has-items', count > 0);
  }
}

if (!customElements.get('compare-icon')) {
  customElements.define('compare-icon', CompareIcon);
}
